import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { heroSlides } from "../mock";
import useBranding from "../hooks/useBranding";
import { brandText } from "../utils/brand";
import { ProductOrbit } from "./hero/ProductOrbit";

const Hero = () => {
  const [step, setStep] = useState(0);
  const b = useBranding();
  const n = heroSlides.length;
  const active = ((step % n) + n) % n;
  const slide = heroSlides[active];

  useEffect(() => {
    const t = setInterval(() => setStep((s) => s + 1), 4500);
    return () => clearInterval(t);
  }, [step]);

  const pick = (i) => {
    let d = i - active;
    if (d > n / 2) d -= n;
    if (d < -n / 2) d += n;
    setStep((s) => s + d);
  };

  const navigate = useNavigate();
  const scrollTo = (href) => (href.startsWith("/") ? navigate(href) : document.querySelector(href)?.scrollIntoView({ behavior: "smooth" }));

  return (
    <section id="top" className="relative pt-[74px] md:pt-[114px]" data-testid="hero-section">
      <div className="relative overflow-hidden bg-[#050C17] min-h-[840px] md:min-h-0 md:h-[700px]">
        <div className="absolute inset-0 pp-hero-grid" />
        <div className="absolute -top-40 -left-32 w-[520px] h-[520px] rounded-full blur-[120px] bg-[#0EA5E9]/25" />
        <div className="absolute -bottom-48 right-[10%] w-[600px] h-[600px] rounded-full blur-[150px] bg-[#10B981]/20" />

        <div className="relative pp-container h-full grid md:grid-cols-[1fr_1.15fr] items-center gap-2 pt-4 pb-14 md:py-0">
          <div className="order-2 md:order-1 z-10">
            <AnimatePresence mode="wait">
              <motion.div
                key={active}
                initial={{ opacity: 0, y: 24, filter: "blur(6px)" }}
                animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                exit={{ opacity: 0, y: -16, filter: "blur(6px)" }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="inline-flex items-center gap-2 text-xs font-bold tracking-[0.2em] uppercase mb-5 px-3 py-1.5 rounded-full border border-[#06B6D4]/30 bg-[#06B6D4]/10 text-[#06B6D4]" data-testid="hero-kicker">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#10B981] animate-pulse" /> {String(active + 1).padStart(2, "0")} · {slide.kicker}
                </div>
                <h1 className="text-white font-extrabold leading-[1.08] text-4xl sm:text-5xl lg:text-6xl tracking-tight" data-testid="hero-title">
                  {slide.title}
                </h1>
                <p className="text-slate-300/80 text-sm md:text-lg mt-5 max-w-xl leading-relaxed">{brandText(slide.desc, b.company_name)}</p>
                <button
                  onClick={() => scrollTo(slide.href)}
                  data-testid="hero-cta-button"
                  className="group mt-8 inline-flex items-center gap-3 text-[#030712] font-semibold pl-7 pr-3 py-3 rounded-full transition-transform hover:scale-[1.03]"
                  style={{ background: "linear-gradient(90deg,#06B6D4,#10B981)" }}
                >
                  {slide.cta}
                  <span className="w-9 h-9 rounded-full bg-[#030712] text-white flex items-center justify-center group-hover:translate-x-1 transition-transform">
                    <ArrowRight size={16} />
                  </span>
                </button>
              </motion.div>
            </AnimatePresence>
            <div className="mt-10 flex flex-wrap gap-2 max-w-xl" data-testid="hero-dots">
              {heroSlides.map((s, i) => (
                <button key={s.name} onClick={() => pick(i)} data-testid={`hero-dot-${i}`}
                  className={`relative overflow-hidden text-[11px] font-semibold px-3 py-1.5 rounded-full border transition-colors ${i === active ? "border-[#06B6D4] text-white" : "border-white/10 text-slate-400 hover:text-white"}`}>
                  {i === active && <span key={step} className="absolute inset-y-0 left-0 bg-[#06B6D4]/25 pp-hero-progress" />}
                  <span className="relative">{s.name}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="order-1 md:order-2 relative h-[380px] md:h-[640px]">
            <ProductOrbit items={heroSlides} step={step} onPick={pick} />
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
